import { useEffect, useMemo, useState } from 'react'

import HyperDX from '@hyperdx/browser'
import { UserDownload, useFetchUserDownloads } from '@queries/fetch-user-downloads'
import { useMarkDownloadAsSeen } from '@queries/mark_download_as_seen'
import { cn } from '@utils/style-utils'

import Button from '@components/core/button'
import { IconButton } from '@components/core/icon-button'
import { Popover, PopoverContent, PopoverTrigger } from '@components/core/popover'
import { Spinner } from '@components/core/spinner'
import { Text } from '@components/core/text'
import Tooltip from '@components/core/tooltip'
import { Cancel, Download, Icon } from '@components/icons'
import LogoIcon from '@components/logo/LogoIcon'

import useAuth from '@hooks/useAuth'

import { Separator } from 'components/core/separator'

import DesktopSection from './desktop-section'

import './index.css'

function getSessionID(sessionURL: string | null): string {
  if (!sessionURL) return ''

  return sessionURL?.split('/s/')[1]?.split('/')[0]
}

const HeaderContent = () => {
  const { isChargerSuperUser } = useAuth()
  const [sessionURL, setSessionURL] = useState<string | null>(null)
  const { data: userDownloads, isLoading: isLoadingUserDownloads } = useFetchUserDownloads()
  const { mutate: markDownloadAsSeen } = useMarkDownloadAsSeen()

  useEffect(() => {
    const sessionURL = HyperDX.getSessionUrl()
    setSessionURL(sessionURL ?? null)
  }, [])

  const [showClipboardTooltip, setShowClipboardTooltip] = useState(false)

  const { unseenDownloads, pendingDownloads, completedDownloads } = useMemo(() => {
    if (isLoadingUserDownloads) return { seenDownloads: [], unseenDownloads: [] }

    const seenDownloads = userDownloads?.filter((download) => download.seen) ?? []
    const unseenDownloads = userDownloads?.filter((download) => !download.seen) ?? []
    const pendingDownloads =
      userDownloads?.filter((download) => download.status === 'pending') ?? []
    const completedDownloads =
      userDownloads?.filter((download) => download.status === 'completed') ?? []
    return { seenDownloads, unseenDownloads, pendingDownloads, completedDownloads }
  }, [userDownloads, isLoadingUserDownloads])

  return (
    <div className='flex items-center'>
      {sessionURL && isChargerSuperUser() && (
        <Tooltip title='Copied to clipboard' open={showClipboardTooltip}>
          <Button
            variant='text'
            className='cursor-pointer focus:border-transparent'
            onClick={() => {
              navigator.clipboard.writeText(sessionURL)
              setShowClipboardTooltip(true)
              setTimeout(() => setShowClipboardTooltip(false), 1000)
            }}
          >
            <Text variant='details'>{getSessionID(sessionURL)}</Text>
          </Button>
        </Tooltip>
      )}

      {userDownloads && userDownloads.length > 0 && (
        <div className='flex items-center px-4'>
          <Popover
            onOpenChange={(open) => {
              if (!open) {
                if (!unseenDownloads || unseenDownloads.length === 0) return

                markDownloadAsSeen({
                  ids: unseenDownloads.map((download: UserDownload) => download.id)
                })
              }
            }}
          >
            <PopoverTrigger asChild>
              <div className='relative'>
                <IconButton
                  variant='outline'
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  className={cn(
                    'download-manager relative rounded-full border-none focus:text-primary-darker',
                    pendingDownloads && pendingDownloads.length && 'download-manager-pending'
                  )}
                >
                  <Icon icon={<Download />} />
                  {completedDownloads && completedDownloads.length > 0 && (
                    <span
                      className='absolute -right-1 -top-0.5 z-10 flex h-4 min-w-[1.15rem] items-center justify-center rounded-full bg-success-dark px-1.5 font-bold text-white shadow'
                      style={{ fontSize: '0.65rem' }}
                    >
                      {completedDownloads.length}
                    </span>
                  )}
                </IconButton>
              </div>
            </PopoverTrigger>
            <PopoverContent className='min-w-96' alignOffset={-300}>
              <div className='flex max-h-96 flex-col overflow-y-auto'>
                {userDownloads.map((download, index) => (
                  <>
                    {index > 0 && <Separator />}
                    <div
                      key={download.id}
                      className='m-2 flex h-9 items-center justify-between gap-2 hover:bg-primary-lighter'
                    >
                      <Text variant='button' className='truncate'>
                        {download.file_name}
                      </Text>

                      {download.status === 'completed' && (
                        <IconButton
                          variant='ghost'
                          className='rounded-md border-none text-success-dark focus:text-primary-darker'
                          onClick={() => {
                            window.open(download.download_url, '_blank')
                          }}
                        >
                          <Icon icon={<Download />} />
                        </IconButton>
                      )}

                      {download.status === 'pending' && (
                        <Spinner className='border-t-primary-dark bg-primary-lighter' />
                      )}

                      {download.status === 'failed' && (
                        <Icon className='text-error-dark' icon={<Cancel />} />
                      )}
                    </div>
                  </>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      <DesktopSection />
      <Separator orientation='vertical' className='h-10' />
      <div className='flex items-center px-4'>
        <LogoIcon className='w-6' />
      </div>
    </div>
  )
}

export default HeaderContent
